<template>
  <div class="shenyunjie-chart-multicurve" :id="id"></div>
</template>

<script>
import { parseTime } from "../../utils/index.js";
import * as echarts from "echarts";
import merge from "deepmerge";

export default {
  name: "YunJieChartMulticurve",
  props: {
    id: {
      type: String,
      default: function () {
        return new Date().getTime() + "-" + Math.ceil(Math.random() * 1000);
      }
    },
    loadChart: {
      type: Boolean,
      default: true
    },
    theme: {
      type: String,
      default: ""
    },
    title: {
      type: String,
      default: ""
    },
    xType: {
      type: String,
      default: "category"
    },
    xData: {
      type: Array,
      default: function () {
        return [];
      }
    },
    yData: {
      type: Array,
      default: function () {
        return [];
      }
    },
    symbol: {
      type: Boolean,
      default: true
    },
    tooltip: {
      type: Boolean,
      default: true
    },
    xLabelFormat: {
      type: String,
      default: ""
    },
    tooltipFormat: {
      type: String,
      default: ""
    },
    dataZoom: {
      type: Boolean,
      default: false
    },
    sampling: {
      type: Boolean,
      default: false
    },
    option: {
      type: Object,
      default: function () {
        return {}
      }
    },
    updateOption: {
      type: Function,
      default: null
    }
  },
  components: {},
  data() {
    return {
      chart: null,
      defaultOption: {
        grid: {
          top: 40,
          left: 60
        },
        title: {
          left: "center"
        }
      },
      units: {},
      resizeObserver: null
    };
  },
  mounted() {
    let self = this;
    // 窗口大小变化监听
    window.addEventListener("resize", self.resizeMulticurve);
    // 元素大小变化监听
    self.resizeObserver = new ResizeObserver((entries) => {
      if (self.chart) {
        self.chart.dispose();
        this.chart = null;
      }
      self.init();
    });
    self.resizeObserver.observe(document.getElementById(self.id));
    if (self.loadChart) {
      self.init();
    }
  },
  methods: {
    resizeMulticurve() {
      let self = this;
      if (self.chart) {
        self.chart.resize();
      }
    },
    init() {
      let self = this;
      // 页面已存在
      if (document.getElementById(self.id) && document.getElementById(self.id).offsetHeight > 0) {
        // 初始化
        if (!self.chart) {
          let theme = self.theme || (this.$YunJie || {}).theme || "standard";
          if (theme == "darkness") {
            self.chart = echarts.init(document.getElementById(self.id), "dark");
          } else {
            self.chart = echarts.init(document.getElementById(self.id));
          }
        }
        self.units = {};
        // 配置汇总
        let parameter = {
          grid: {
            bottom: self.dataZoom ? 70 : 32
          },
          title: {
            text: self.title
          },
          tooltip: {
            show: self.tooltip,
            trigger: "axis",
            formatter: function (params) {
              let str = "";
              let format = self.tooltipFormat ? self.tooltipFormat : (self.xType == "time" ? "{yyyy}-{MM}-{dd} {HH}:{mm}:{ss}.{SSS}" : "");
              params.forEach((item, idx) => {
                if (idx === 0) {
                  if (format && self.xType == "time") {
                    str += `${parseTime(new Date(item.axisValue), format)}<br/>`;
                  } else {
                    str += `${item.axisValue}<br/>`;
                  }
                }
                let name = item.seriesName ? `${item.seriesName}：` : "";
                let value = "";
                let unit = self.units[idx] ? ` ${self.units[idx]}` : "";
                if (self.xType == "time") {
                  value = item.data[1];
                } else {
                  if (format) {
                    value = parseFloat(item.data).toFixed(parseInt(format));
                  } else {
                    value = item.data;
                  }
                }
                str += `${item.marker} ${name}${value}${unit}`;
                str += idx === params.length - 1 ? "" : "<br/>";
              });
              return str;
            }
          },
          dataZoom: self.dataZoom ? [{
            type: "inside"
          }, {
            type: "slider"
          }] : []
        };
        let opt = merge(self.defaultOption, parameter);
        if (self.xData.length > 0 || self.yData.length > 0) {
          opt.series = [];
          opt.xAxis = {
            type: self.xType,
            data: self.xData,
            axisLabel: {
              formatter: self.xLabelFormat ? self.xLabelFormat : (self.xType == "time" ? "{yyyy}-{MM}-{dd} {HH}:{mm}:{ss}" : "{value}")
            }
          };
          opt.yAxis = [];
          let _yAxis = {};
          for (let i = 0; i < self.yData.length; i++) {
            let _series = merge({}, self.yData[i]);
            // 是否存在单位
            if (_series.unit) {
              self.units[i] = _series.unit;
            }
            // 是否独立y轴
            if (_series.yAxis) {
              _series.yAxisIndex = opt.yAxis.length;
              if (opt.yAxis.length > 1) {
                _series.yAxis.offset = 60 * (opt.yAxis.length - 1);
              }
              opt.yAxis.push(_series.yAxis);
            } else {
              if (_yAxis[_series.unit] == undefined) {
                _yAxis[_series.unit] = opt.yAxis.length;
                let y = {
                  type: "value",
                  axisLine: {
                    show: true
                  },
                  name: _series.unit ? `(单位：${_series.unit})` : "",
                  scale: true
                }
                if (_yAxis[_series.unit] > 1) {
                  y.offset = 60 * (_yAxis[_series.unit] - 1);
                }
                opt.yAxis.push(y);
              }
              _series.yAxisIndex = _yAxis[_series.unit];
            }
            // 是否显示数据点
            _series.symbol = self.symbol ? "circle" : "none";
            // 是否为面积图
            if (_series.type == "area") {
              _series.type = "line";
              _series.areaStyle = {};
            }
            // 是否优化大数据显示
            if (self.sampling) {
              _series.sampling = "lttb";
            }
            // 补全参数
            if (!_series.type) {
              _series.type = "line";
            }
            opt.series.push(_series);
          }
          if (opt.yAxis.length == 0) {
            opt.yAxis = {
              type: "value",
              axisLine: {
                show: true
              }
            };
            opt.grid.right = 24;
          } else {
            opt.grid.right = opt.yAxis.length > 1 ? 60 * (opt.yAxis.length - 1) : 24;
          }
        }
        // 合并自定义配置
        opt = merge(opt, self.option);
        // 用户自定义属性
        if (self.updateOption) {
          opt = self.updateOption(opt);
        }
        self.chart.setOption(opt, true);
        self.chart.resize();
      }
    }
  },
  destroyed() {
    window.removeEventListener("resize", this.resizeMulticurve);
    this.resizeObserver.disconnect();
  },
  watch: {
    theme() {
      // 销毁已有曲线
      if (this.chart) {
        this.chart.dispose();
        this.chart = null;
      }
      this.init();
    },
    title() {
      this.init();
    },
    xType() {
      this.init();
    },
    xData: {
      deep: true,
      handler(xData) {
        this.init();
      }
    },
    yData: {
      deep: true,
      handler(yData) {
        this.init();
      }
    },
    symbol() {
      this.init();
    },
    tooltip() {
      this.init();
    },
    xLabelFormat() {
      this.init();
    },
    tooltipFormat() {
      this.init();
    },
    dataZoom() {
      this.init();
    },
    sampling() {
      this.init();
    },
    option: {
      deep: true,
      handler(option) {
        this.init();
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.shenyunjie-chart-multicurve {
  height: 100%;
  width: 100%;
}
</style>


// WEBPACK FOOTER //
// src/components/charts/multicurve.vue